import { useLazyQuery, useQuery } from '@apollo/react-hooks';
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import ReactTooltip from 'react-tooltip';
import {
  GET_CAMPAIGN,
  GET_CAMPAIGN_CONFIG,
  GET_CURRENT_ORGANIZATION,
} from '../../graphql/queries';
import Container from '../../shared/Container';
import CampaignForm, { campaignStatus } from './CampaignForm';
import Section from '../../shared/Section';
import colors from '../../styles/colors';
import * as Price from '../../util/Price';
import {
  CampaignFormType,
  buildReplicateInitialValues,
} from '../../util/Campaign';

const BackButton = styled.div`
  cursor: pointer;
  z-index: 30;
  display: flex;
  position: absolute;
  right: 20px;
  top: -60px;
  @media screen and (max-width: 450px) {
    right: 0;
  }
`;

const ToolbarItem = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.2s;
  font-weight: 600;
  color: ${colors.seaGreen};
  font-size: 1.4rem;
  cursor: pointer;
  width: max-content;
  border-radius: 10px;
  padding: 8px 27px;
  background: rgba(26, 188, 156, 0.1);
  @media screen and (max-width: 991px) {
    ${(props) => props.headerDirection === 'column' && 'margin-top:10px'};
  }
  @media screen and (max-width: 767px) {
    padding: 2vw 6vw;
  }  
`;

const CreateCampaign = ({ history }) => {
  const { data: organizationData } = useQuery(GET_CURRENT_ORGANIZATION);
  const { getCurrentOrganization: currentOrganisation } = organizationData || {};
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const sourceCampaignId = searchParams.get('id');
  const isReplicate = !!sourceCampaignId;

  const [getCampaign, { data: campaignData, loading: campaignLoading }] = useLazyQuery(
    GET_CAMPAIGN,
    {
      fetchPolicy: 'network-only',
      variables: {
        campaignId: sourceCampaignId,
      },
    },
  );
  const { data, loading: configLoading } = useQuery(GET_CAMPAIGN_CONFIG, {
    fetchPolicy: 'network-only',
  });

  const orgName = currentOrganisation && currentOrganisation.name
    ? currentOrganisation.name
    : '';
  const referralText = currentOrganisation
    ? `Click this link to text your friends up to a $${Price.output(
      currentOrganisation.configurations?.firstTimeRecipientCredit,
      true,
    )} discount to ${orgName} and you can earn $${Price.output(
      currentOrganisation.configurations?.affiliateCredit,
      true,
    )} credit at ${orgName} when their discount is redeemed: ${window.location.origin
    }/user/invite/?o=${currentOrganisation._id
    }. It's so simple and you can send as many discounts as you would like!`
    : '';

  const unsubscribeMessage = orgName
    ? `Reply STOP to stop receiving update texts from ${orgName}.`
    : '';

  const blankValues = {
    name: '',
    text: '',
    unsubscribeMessage,
    status: campaignStatus.Draft,
    type: 'SMS',
    imageUrl: '',
    referralOptIn: false,
    referralOptInText: referralText,
    isUrlShortningEnabled: true,
    tags: [],
    events: [],
    isTriggerCampaign: false,
  };

  const [values, setValues] = useState(null);

  useEffect(() => {
    if (sourceCampaignId) {
      getCampaign();
    }
  }, [sourceCampaignId, getCampaign]);

  // Fresh create: seed blank defaults once org copy is available.
  useEffect(() => {
    if (isReplicate || !currentOrganisation || values) {
      return;
    }
    setValues({
      ...blankValues,
      unsubscribeMessage,
      referralOptInText: referralText,
    });
  }, [isReplicate, currentOrganisation, values, unsubscribeMessage, referralText]);

  // Replicate: seed exactly once from source campaign + current org referral template.
  useEffect(() => {
    if (!isReplicate || !campaignData || !campaignData.campaign || values) {
      return;
    }
    if (!currentOrganisation) {
      return;
    }
    setValues(buildReplicateInitialValues(campaignData.campaign, {
      referralText,
      unsubscribeFallback: unsubscribeMessage,
      draftStatus: campaignStatus.Draft,
    }));
  }, [
    isReplicate,
    campaignData,
    currentOrganisation,
    values,
    referralText,
    unsubscribeMessage,
  ]);

  const formReady = !!values
    && !!currentOrganisation
    && !configLoading
    && (!isReplicate || (!campaignLoading && !!campaignData?.campaign));

  return (
    <Container>
      <Section
        headerTitle={isReplicate ? 'Replicate Campaign' : 'Create Campaign'}
        loading={!formReady}
        justify="flex-start"
        width="50%"
      >
        <BackButton
          onClick={() => history.push('/campaigns')}
          data-tip="Back To Campaigns List"
        >
          <ToolbarItem>Cancel</ToolbarItem>
        </BackButton>
        {formReady && (
          <div data-help="create-campaign-form-root">
            <CampaignForm
              mmsEnabled={data?.getDataForCampaign?.mmsEnabled}
              activeContacts={data?.getDataForCampaign?.activeContacts}
              formType={isReplicate ? CampaignFormType.Replicate : CampaignFormType.Add}
              values={values}
              setValues={setValues}
              shortUrlDomain={data?.getDataForCampaign?.shortUrlDomain}
            />
          </div>
        )}
      </Section>
      <ReactTooltip
        place="right"
        type="dark"
        effect="solid"
        clickable={false}
        multiline={true}
      />
    </Container>
  );
};

export default CreateCampaign;
